import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { homePathFor, useAuth } from './lib/auth';
import { AppShell } from './components/AppShell';
import { PlanGate } from './components/PlanGate';
import { Loading, ErrorState, EmptyState } from './components/States';
import { PageHeader } from './components/Page';
import { LoginPage } from './pages/Login';
import { NotFound, FullPageMessage } from './pages/NotFound';
import type { Role } from './lib/types';
import { APP_HOME_BASENAME, APP_HOME_PATH, isEmbeddedAdmin } from './lib/embedded';
import { ApiError } from './lib/api';
import { isSitePath, siteTagsActive } from './pages/site/navigation';
import { SITE_PAGES } from './pages/site/pages';
import { useAdminFeatures, useAdminRedesign } from './lib/adminFeatures';

/** Admin home behind the redesign release flag (PERZO_ADMIN_REDESIGN). */
function AdminHome() {
  const redesign = useAdminRedesign();
  if (redesign === null) return <Loading />;
  return redesign ? <AdminOverview /> : <AdminOverviewLegacy />;
}

const MerchantOverview = lazy(() => import('./pages/merchant/Overview'));
const ProductEditPage = lazy(() => import('./pages/merchant/ProductEdit'));
const NewProductPage = lazy(() => import('./pages/merchant/setupWizard/NewProduct'));
const MockupsPage = lazy(() => import('./pages/merchant/Mockups'));
const LegacyProductsRedirect = lazy(() => import('./pages/merchant/Mockups').then((m) => ({ default: m.LegacyProductsRedirect })));
const StylesPage = lazy(() => import('./pages/merchant/Styles'));
const StyleStudioPage = lazy(() => import('./pages/merchant/studio/StyleStudio'));
const StyleWizardPage = lazy(() => import('./pages/merchant/studio/StyleWizard'));
const OrdersPage = lazy(() => import('./pages/merchant/Orders'));
const OrderDetailPage = lazy(() => import('./pages/merchant/OrderDetail'));
const ManualArtPage = lazy(() => import('./pages/merchant/ManualArt'));
const GenerationsPage = lazy(() => import('./pages/merchant/Generations'));
const FilesPage = lazy(() => import('./pages/merchant/Files'));
const AnalyticsPage = lazy(() => import('./pages/merchant/Analytics'));
const BillingPage = lazy(() => import('./pages/merchant/Billing'));
const SettingsPage = lazy(() => import('./pages/merchant/Settings'));
const WizardSettingsPage = lazy(() => import('./pages/merchant/WizardSettings'));
const WizardFunnelPage = lazy(() => import('./pages/merchant/WizardFunnel'));
const IntegrationsPage = lazy(() => import('./pages/merchant/Integrations'));
const SetupPage = lazy(() => import('./pages/merchant/Setup'));
const FeedbackPage = lazy(() => import('./pages/merchant/Feedback'));

const AdminOverview = lazy(() => import('./pages/admin/Overview'));
const AdminOverviewLegacy = lazy(() => import('./pages/admin/OverviewLegacy'));
const AdminMerchants = lazy(() => import('./pages/admin/Merchants'));
const AdminMerchantDetail = lazy(() => import('./pages/admin/MerchantDetail'));
const AdminBilling = lazy(() => import('./pages/admin/Billing'));
const AdminAffiliates = lazy(() => import('./pages/admin/Affiliates'));
const AdminAiCosts = lazy(() => import('./pages/admin/AiCosts'));
const AdminBenchmarks = lazy(() => import('./pages/admin/Benchmarks'));
const AdminShopperWait = lazy(() => import('./pages/admin/ShopperWait'));
const AdminModels = lazy(() => import('./pages/admin/Models'));
const AdminSystem = lazy(() => import('./pages/admin/System'));
const AdminAlerts = lazy(() => import('./pages/admin/Alerts'));
const AdminStatusIncidents = lazy(() => import('./pages/admin/StatusIncidents'));
const AdminSupport = lazy(() => import('./pages/admin/Support'));
const AdminAudit = lazy(() => import('./pages/admin/Audit'));
const AdminEarlyAccess = lazy(() => import('./pages/admin/EarlyAccess'));
const GrowthPage = lazy(() => import('./pages/growth/Growth'));
export const ADMIN_GROWTH_ROUTE_PATHS = ['growth', 'growth/:tab'] as const;

/**
 * The affiliate page (PERZO_AFFILIATES) only when the server advertises it; otherwise a clear "off"
 * page (it was blank: A1 homologation, 18/10).
 */
export function AdminAffiliatesRoute() {
  const features = useAdminFeatures();
  if (features.data?.affiliates === true) return <Lazy><AdminAffiliates /></Lazy>;
  if (!features.data && !features.error) return <Loading />;
  // A failed flag check is an error to retry, not "off".
  if (!features.data && features.error) return <ErrorState error={features.error} onRetry={() => void features.refetch()} context="afiliados" />;
  return (
    <>
      <PageHeader title="Afiliados" />
      <EmptyState title="Programa de afiliados desligado">
        Ligue PERZO_AFFILIATES no servidor para ver links, lojas indicadas e comissões.
      </EmptyState>
    </>
  );
}

const PersonalizePage = lazy(() => import('./pages/storefront/Personalize'));
const MyArtPage = lazy(() => import('./pages/share/MyArt'));
const SharedArtPage = lazy(() => import('./pages/share/SharedArt'));
const ShareManagePage = lazy(() => import('./pages/share/ShareManage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvite'));

function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { status, me, error, refresh } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageMessage><Loading label="Checking your session…" /></FullPageMessage>;
  if (status === 'error' && error) {
    return (
      <FullPageMessage>
        <ErrorState error={error} onRetry={() => void refresh()} context="session" />
      </FullPageMessage>
    );
  }
  if (!me) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (me.user.role !== role) {
    // The server authorizes every request; this redirect only keeps the UI coherent.
    return <Navigate to={homePathFor(me)} replace />;
  }
  return <>{children}</>;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Public pages paint their own layout; an empty white frame avoids a spinner flash. */
function Site({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="site-loading" aria-busy="true" />}>{children}</Suspense>;
}

/**
 * Merchant pages, shared verbatim by the www.perzo.art dashboard (/app) and Shopify admin
 * (/shopify/app): both read and write the same tenant records through the same API.
 */
const MERCHANT_ROUTES = (
  <>
    <Route index element={<Lazy><MerchantOverview /></Lazy>} />
    {/* Products and Mockups are one place (MOCKUP-FIRST.md, stage 7); the old addresses redirect. */}
    <Route path="mockups" element={<Lazy><MockupsPage /></Lazy>} />
    <Route path="mockups/new" element={<Lazy><NewProductPage /></Lazy>} />
    <Route path="mockups/:productId" element={<Lazy><ProductEditPage /></Lazy>} />
    <Route path="products" element={<Lazy><LegacyProductsRedirect to="list" /></Lazy>} />
    <Route path="products/new" element={<Lazy><LegacyProductsRedirect to="new" /></Lazy>} />
    <Route path="products/:productId" element={<Lazy><LegacyProductsRedirect to="edit" /></Lazy>} />
    <Route path="styles" element={<Lazy><StylesPage /></Lazy>} />
    <Route path="styles/new" element={<Lazy><StyleWizardPage /></Lazy>} />
    <Route path="styles/:styleId" element={<Lazy><StyleStudioPage /></Lazy>} />
    <Route path="styles/:styleId/wizard" element={<Lazy><StyleWizardPage /></Lazy>} />
    <Route path="orders" element={<Lazy><OrdersPage /></Lazy>} />
    <Route path="orders/:orderId" element={<Lazy><OrderDetailPage /></Lazy>} />
    <Route path="manual-art" element={<Lazy><ManualArtPage /></Lazy>} />
    <Route path="generations" element={<Lazy><GenerationsPage /></Lazy>} />
    <Route path="files" element={<Lazy><FilesPage /></Lazy>} />
    <Route path="analytics" element={<Lazy><AnalyticsPage /></Lazy>} />
    <Route path="billing" element={<Lazy><BillingPage /></Lazy>} />
    <Route path="settings" element={<Lazy><SettingsPage /></Lazy>} />
    <Route path="wizard" element={<Lazy><WizardSettingsPage /></Lazy>} />
    <Route path="funnel" element={<Lazy><WizardFunnelPage /></Lazy>} />
    <Route path="integrations" element={<Lazy><IntegrationsPage /></Lazy>} />
    <Route path="setup" element={<Lazy><SetupPage /></Lazy>} />
    <Route path="support" element={<Lazy><FeedbackPage /></Lazy>} />
    <Route path="*" element={<NotFound inline />} />
  </>
);

/** Inside Shopify admin there is no Perzo sign-in: Shopify's session is the only way in. */
function RequireShopifyStaff({ children }: { children: ReactNode }) {
  const { status, me, error, refresh } = useAuth();
  if (status === 'loading') return <FullPageMessage><Loading label="Connecting to Shopify…" /></FullPageMessage>;
  if (error || !me || me.user.role !== 'merchant' || me.channel !== 'shopify_admin') {
    return (
      <FullPageMessage>
        <ErrorState error={error ?? new ApiError(401, 'shopify_session_required', 'Reload Perzo from Shopify admin to continue.')} onRetry={() => void refresh()} context="session" />
      </FullPageMessage>
    );
  }
  return <>{children}</>;
}

/** App Bridge nav menu clicks: keep client-side routing (no full reload) when the event is available. */
function useShopifyNavigate() {
  const navigate = useNavigate();
  useEffect(() => {
    const onNavigate = (event: Event) => {
      const href = event.target instanceof Element ? event.target.getAttribute('href') : null;
      if (!href || (href !== APP_HOME_PATH && !href.startsWith(`${APP_HOME_PATH}/`))) return;
      event.preventDefault();
      navigate(href.slice(APP_HOME_BASENAME.length));
    };
    document.addEventListener('shopify:navigate', onNavigate);
    return () => document.removeEventListener('shopify:navigate', onNavigate);
  }, [navigate]);
}

function EmbeddedApp() {
  useShopifyNavigate();
  return (
    <Routes>
      <Route
        path="/app"
        element={
          <RequireShopifyStaff>
            <AppShell area="merchant" />
          </RequireShopifyStaff>
        }
      >
        {/* No usable plan: plan selection first (plans, settings and support stay open). */}
        <Route element={<PlanGate />}>{MERCHANT_ROUTES}</Route>
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

/**
 * Google tags load only on the public site pages. If this document has them and the app moves to
 * any other page without a click the site pages catch (the browser's Back button, a programmatic
 * navigation), GA4 is stopped and the page loads again, fresh and without tags.
 */
function useLeaveTaggedDocument() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!siteTagsActive() || isSitePath(pathname)) return;
    window.__perzoSiteTags?.stop();
    window.location.reload();
  }, [pathname]);
}

export function App() {
  if (isEmbeddedAdmin()) return <EmbeddedApp />;
  return <StandaloneApp />;
}

/** www.perzo.art: the public site, login, the merchant dashboard (/app), admin and the wizard page. */
function StandaloneApp() {
  useLeaveTaggedDocument();
  return (
    <Routes>
      {/* The public pages: one registry (shared/siteRoutes.ts), also prerendered by the server (entry-server.tsx). */}
      {Object.entries(SITE_PAGES).map(([path, Page]) => (
        <Route key={path} path={path} element={<Site><Page /></Site>} />
      ))}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<Lazy><AcceptInvitePage /></Lazy>} />
      {/* Buyer art page and share links (domain/buyerArt.ts): app shells, noindex. */}
      <Route path="/my-art" element={<Lazy><MyArtPage /></Lazy>} />
      <Route path="/share/manage" element={<Lazy><ShareManagePage /></Lazy>} />
      <Route path="/share/:token" element={<Lazy><SharedArtPage /></Lazy>} />
      <Route
        path="/personalize/:productId"
        element={
          <Suspense fallback={<FullPageMessage><Loading /></FullPageMessage>}>
            <PersonalizePage />
          </Suspense>
        }
      />

      <Route
        path="/app"
        element={
          <RequireRole role="merchant">
            <AppShell area="merchant" />
          </RequireRole>
        }
      >
        {MERCHANT_ROUTES}
      </Route>

      <Route
        path="/admin"
        element={
          <RequireRole role="admin">
            <AppShell area="admin" />
          </RequireRole>
        }
      >
        <Route index element={<Lazy><AdminHome /></Lazy>} />
        <Route path="merchants" element={<Lazy><AdminMerchants /></Lazy>} />
        <Route path="merchants/:merchantId" element={<Lazy><AdminMerchantDetail /></Lazy>} />
        <Route path="billing" element={<Lazy><AdminBilling /></Lazy>} />
        <Route path="affiliates" element={<AdminAffiliatesRoute />} />
        <Route path="ai-costs" element={<Lazy><AdminAiCosts /></Lazy>} />
        <Route path="benchmarks" element={<Lazy><AdminBenchmarks /></Lazy>} />
        <Route path="shopper-wait" element={<Lazy><AdminShopperWait /></Lazy>} />
        <Route path="models" element={<Lazy><AdminModels /></Lazy>} />
        <Route path="system" element={<Lazy><AdminSystem /></Lazy>} />
        <Route path="alerts" element={<Lazy><AdminAlerts /></Lazy>} />
        <Route path="status" element={<Lazy><AdminStatusIncidents /></Lazy>} />
        <Route path="support" element={<Lazy><AdminSupport /></Lazy>} />
        <Route path="audit" element={<Lazy><AdminAudit /></Lazy>} />
        <Route path="early-access" element={<Lazy><AdminEarlyAccess /></Lazy>} />
        {ADMIN_GROWTH_ROUTE_PATHS.map((path) => (
          <Route key={path} path={path} element={<Lazy><GrowthPage /></Lazy>} />
        ))}
        <Route path="*" element={<NotFound inline />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
